import React, { useEffect, useRef, useState } from 'react';
import './BeforeAfter.css';

/* ── Helpers ── */

function useInView(ref, threshold = 0.2) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return visible;
}

function CountUp({ target, suffix = '', duration = 1500, delay = 0 }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReduced) {
      setValue(target);
      return;
    }
    const timer = setTimeout(() => {
      const start = performance.now();
      function tick(now) {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(eased * target));
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(timer);
  }, [visible, target, duration, delay]);

  return (
    <span ref={ref} className="ba-stat-number">
      {value}
      {suffix}
    </span>
  );
}

/* ── SVG icons (inline, no deps) ── */

function ClockIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function DocIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function PhoneIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

function TrophyIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 010-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0012 0V2z" />
    </svg>
  );
}

function CheckSmall() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XSmall() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ── Left-side sub-components ── */

function SheetsIcon({ size = 16 }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        background: '#0F9D58',
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
  );
}

function GoogleSheetsWindow({ deadRows = new Set(), highlightedRow = null }) {
  const tabs = [
    { name: 'LinkedIn J...', active: false, warning: false },
    { name: 'Indeed', active: false, warning: false },
    { name: 'Handshake', active: false, warning: false },
    { name: '\u26A0 Glassd...', active: false, warning: true },
    { name: 'Wellfound', active: false, warning: false },
    { name: 'Lever', active: false, warning: false },
    { name: 'Greenhouse', active: false, warning: false },
    { name: 'Google Sheets - Job A...', active: true, warning: false },
  ];

  const columns = [
    { letter: 'A', header: 'Company', width: 120 },
    { letter: 'B', header: 'Role', width: 140 },
    { letter: 'C', header: 'Location', width: 110 },
    { letter: 'D', header: 'Status', width: 90 },
    { letter: 'E', header: 'Date Applied', width: 85 },
    { letter: 'F', header: 'Recruiter Email', width: 150 },
    { letter: 'G', header: 'Follow-up?', width: 80 },
    { letter: 'H', header: 'Notes', width: 130 },
  ];

  const rows = [
    {
      num: 1,
      cells: [
        { v: 'Google' },
        { v: 'SWE Intern' },
        { v: 'Mountain View, CA' },
        { v: 'Applied \u2713', bg: '#D1FAE5', color: '#059669' },
        { v: '03/12' },
        { v: 'recruiting@google.com' },
        { v: 'Yes' },
        { v: 'Followed up 3/15' },
      ],
    },
    {
      num: 2,
      cells: [
        { v: 'Jane Street' },
        { v: 'Quant Dev' },
        { v: 'NYC' },
        { v: 'Rejected', bg: '#FEE2E2', color: '#DC2626' },
        { v: '03/10' },
        { v: '\u2014' },
        { v: '\u2014' },
        { v: 'form rejection' },
      ],
    },
    {
      num: 3,
      cells: [
        { v: 'Citadel' },
        { v: 'Quant Researcher' },
        { v: 'Chicago' },
        { v: '???', bg: '#FEF9C3', color: '#92400E', selected: true },
        { v: '03/08' },
        { v: '' },
        { v: 'No' },
        { v: '' },
      ],
    },
    {
      num: 4,
      cells: [
        { v: 'Meta' },
        { v: 'ML Intern' },
        { v: 'Menlo Park, CA' },
        { v: 'No reply', bg: '#FFEDD5', color: '#C2410C' },
        { v: '03/05' },
        { v: 'careers@meta.com' },
        { v: 'Yes (x2)' },
        { v: 'emailed twice, nothing', italic: true },
      ],
    },
    {
      num: 5,
      cells: [
        { v: 'Two Sigma' },
        { v: 'Quant Dev' },
        { v: 'NYC' },
        { v: 'Applied' },
        { v: '03/02' },
        { v: '???' },
        { v: 'No' },
        { v: 'which portal was this?' },
      ],
    },
    {
      num: 6,
      cells: [
        { v: 'Apple' },
        { v: 'iOS Engineer' },
        { v: 'Cupertino' },
        { v: 'Pending' },
        { v: '02/28' },
        { v: '\u2014' },
        { v: '\u2014' },
        { v: 'need to follow up' },
      ],
    },
    {
      num: 7,
      cells: [
        { v: 'DE Shaw' },
        { v: '' },
        { v: '' },
        { v: '' },
        { v: '' },
        { v: '' },
        { v: '' },
        { v: '' },
      ],
    },
    {
      num: 8,
      cells: [
        { v: 'Google' },
        { v: 'SWE Intern' },
        { v: 'Mountain View, CA' },
        { v: 'Applied?' },
        { v: '03/12' },
        { v: '' },
        { v: '' },
        { v: 'wait did I already apply?' },
      ],
    },
  ];

  const fadeRows = [
    { num: 9, opacity: 0.8 },
    { num: 10, opacity: 0.6 },
    { num: 11, opacity: 0.4 },
    { num: 12, opacity: 0.2 },
  ];

  const getRowClassName = (row) => {
    let cls = 'gs-data-row';
    if (row.struck && !deadRows.has(row.num)) cls += ' gs-data-row--struck';
    if (deadRows.has(row.num)) cls += ' gs-row--dead';
    if (highlightedRow === row.num) cls += ' gs-row--highlighted';
    return cls;
  };

  return (
    <div className="gs-window">
      {/* Browser tab bar */}
      <div className="gs-tabbar">
        <div className="gs-dots">
          <span className="gs-dot gs-dot--red" />
          <span className="gs-dot gs-dot--yellow" />
          <span className="gs-dot gs-dot--green" />
        </div>
        <div className="gs-tabs">
          {tabs.map((t) => (
            <div
              key={t.name}
              className={`gs-tab ${t.active ? 'gs-tab--active' : ''}`}
            >
              {t.active && <SheetsIcon size={10} />}
              {t.warning && <span className="gs-tab-warn">{'\u26A0'}</span>}
              <span className="gs-tab-label">{t.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* URL bar */}
      <div className="gs-urlbar">
        <span className="gs-url-text">docs.google.com/spreadsheets/d/1a2b3c...</span>
      </div>

      {/* Sheets title bar */}
      <div className="gs-title-bar">
        <div className="gs-title-left">
          <SheetsIcon size={16} />
          <span className="gs-title-text">Job Applications Tracker</span>
        </div>
        <div className="gs-title-right">
          <span className="gs-share-btn">Share</span>
          <span className="gs-avatar">J</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="gs-toolbar">
        <span className="gs-toolbar-icon" />
        <span className="gs-toolbar-icon" />
        <span className="gs-toolbar-icon" />
        <span className="gs-toolbar-icon" />
        <span className="gs-toolbar-icon" />
      </div>

      {/* Formula bar */}
      <div className="gs-formula-bar">
        <span className="gs-cell-ref">A1</span>
        <span className="gs-formula-text">=VLOOKUP(...</span>
      </div>

      {/* Column letters */}
      <div className="gs-col-letters">
        <span className="gs-row-num-header" />
        {columns.map((c) => (
          <span key={c.letter} className="gs-col-letter" style={{ width: c.width }}>{c.letter}</span>
        ))}
      </div>

      {/* Column headers */}
      <div className="gs-header-row">
        <span className="gs-row-num-header" />
        {columns.map((c) => (
          <span key={c.header} className="gs-header-cell" style={{ width: c.width }}>{c.header}</span>
        ))}
      </div>

      {/* Frozen header border */}
      <div className="gs-frozen-border" />

      {/* Data rows */}
      <div className="gs-data-area">
        {rows.map((row) => {
          const isDead = deadRows.has(row.num);
          return (
            <div
              key={row.num}
              className={getRowClassName(row)}
            >
              <span className="gs-row-num">{row.num}</span>
              {row.cells.map((cell, i) => {
                const isStatusCol = i === 3;
                const deadStatus = isDead && isStatusCol;
                return (
                  <span
                    key={i}
                    className={`gs-cell ${cell.selected && !isDead ? 'gs-cell--selected' : ''}`}
                    style={{
                      width: columns[i].width,
                      background: deadStatus ? '#FEE2E2' : (isDead ? 'transparent' : (cell.bg || 'transparent')),
                      color: deadStatus ? '#DC2626' : (isDead ? '#D1D5DB' : (cell.color || (row.struck ? '#9CA3AF' : '#374151'))),
                      fontStyle: cell.italic ? 'italic' : 'normal',
                      fontSize: cell.italic ? 11 : undefined,
                    }}
                  >
                    {deadStatus ? 'Rejected' : cell.v}
                  </span>
                );
              })}
            </div>
          );
        })}
        {fadeRows.map((fr) => {
          const isDead = deadRows.has(fr.num);
          return (
            <div
              key={fr.num}
              className={`gs-data-row${isDead ? ' gs-row--dead' : ''}${highlightedRow === fr.num ? ' gs-row--highlighted' : ''}`}
              style={{ opacity: isDead ? 1 : fr.opacity }}
            >
              <span className="gs-row-num">{fr.num}</span>
              {columns.map((c, i) => {
                const isStatusCol = i === 3;
                const deadStatus = isDead && isStatusCol;
                return (
                  <span
                    key={i}
                    className="gs-cell"
                    style={{
                      width: c.width,
                      background: deadStatus ? '#FEE2E2' : 'transparent',
                      color: deadStatus ? '#DC2626' : (isDead ? '#D1D5DB' : '#374151'),
                    }}
                  >
                    {deadStatus ? 'Rejected' : '\u00A0'}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Scrollbar */}
      <div className="gs-scrollbar">
        <div className="gs-scrollbar-thumb" />
      </div>

      {/* Bottom sheet tabs */}
      <div className="gs-sheet-tabs">
        <span className="gs-sheet-tab gs-sheet-tab--active">Applications</span>
        <span className="gs-sheet-tab">
          Rejections
          <span className="gs-sheet-badge">12</span>
        </span>
      </div>
    </div>
  );
}

/* Card-to-row mapping */
const CARD_TO_ROW = [2, 4, 6, 7, 3, 5, 8, 9, 10, 11, 12, 1];

const REJECTION_CARDS = [
  {
    company: 'Jane Street',
    email: 'Dear applicant, after careful review we have decided not to move forward...',
    pill: 'Rejected',
    pillBg: '#FEE2E2',
    pillColor: '#DC2626',
    time: '2 hours ago',
  },
  {
    company: 'Meta',
    email: 'Thank you for your interest in the ML Intern position. Unfortunately...',
    pill: 'Rejected',
    pillBg: '#FEE2E2',
    pillColor: '#DC2626',
    time: '5 hours ago',
  },
  {
    company: 'Apple',
    email: 'We appreciate your application for Data Engineering. After reviewing all candidates...',
    pill: 'Rejected',
    pillBg: '#FEE2E2',
    pillColor: '#DC2626',
    time: 'Yesterday',
  },
  {
    company: 'DE Shaw',
    email: 'The position you applied for has been filled...',
    pill: 'No Response',
    pillBg: '#FEF3C7',
    pillColor: '#92400E',
    time: '2 days ago',
  },
  {
    company: 'Citadel',
    email: 'We regret to inform you that we are unable to offer you a position...',
    pill: 'Rejected',
    pillBg: '#FEE2E2',
    pillColor: '#DC2626',
    time: '3 days ago',
  },
  { company: 'Two Sigma', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
  { company: 'Google (dup)', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
  { company: 'HRT', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
  { company: 'Bridgewater', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
  { company: 'Amazon', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
  { company: 'Microsoft', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
  { company: 'Stripe', pill: 'Rejected', pillBg: '#FEE2E2', pillColor: '#DC2626', rapid: true },
];

function RejectionStack({ currentCard, rejPhase, promptVisible, onDismiss, onShowAll, onReplay }) {
  const [exitingCard, setExitingCard] = useState(null);

  const handleClick = () => {
    if (rejPhase !== 'clicking' || exitingCard !== null) return;
    setExitingCard(currentCard);
    setTimeout(() => {
      setExitingCard(null);
      onDismiss();
    }, 300);
  };

  /* Phase: aftermath */
  if (rejPhase === 'aftermath') {
    return (
      <div className="rejection-stack">
        <div className="rejection-aftermath">
          <span className="rejection-aftermath-text">More coming tomorrow...</span>
        </div>
        <button className="rejection-replay-btn" onClick={onReplay}>
          &#8635; Replay
        </button>
      </div>
    );
  }

  /* Phase: pause */
  if (rejPhase === 'pause') {
    return (
      <div className="rejection-stack">
        <div className="rejection-pause-overlay">
          <span className="rejection-pause-text">7 more rejections waiting...</span>
          <button className="rejection-showall-btn" onClick={onShowAll}>
            See them all &rarr;
          </button>
        </div>
      </div>
    );
  }

  /* Phase: rapid - show current rapid card */
  if (rejPhase === 'rapid') {
    const card = REJECTION_CARDS[currentCard];
    if (!card) return null;
    return (
      <div className="rejection-stack">
        <div className="rejection-counter">
          {currentCard + 1} of 12
        </div>
        <div className="rejection-card rejection-card--front rejection-card--rapid">
          <div className="rejection-card-topbar">
            <span className="gs-rej-dot" /><span className="gs-rej-dot" /><span className="gs-rej-dot" />
          </div>
          <span className="rejection-card-company">{card.company}</span>
          <span
            className="rejection-card-pill"
            style={{ background: card.pillBg, color: card.pillColor }}
          >
            {card.pill}
          </span>
        </div>
      </div>
    );
  }

  /* Phase: clicking - interactive stack */
  const remaining = 12 - currentCard;
  const visibleCards = [];
  for (let offset = 0; offset < Math.min(3, remaining); offset++) {
    const idx = currentCard + offset;
    if (idx >= REJECTION_CARDS.length) break;
    visibleCards.push({ ...REJECTION_CARDS[idx], idx, offset });
  }

  return (
    <div className="rejection-stack" onClick={handleClick} style={{ cursor: 'pointer' }}>
      <div className="rejection-counter">
        {currentCard + 1} of 12
      </div>
      {promptVisible && (
        <span className="rejection-prompt">Click to see more &rarr;</span>
      )}
      <div className="rejection-stack-cards">
        {visibleCards.map((card) => {
          const isExiting = exitingCard === card.idx;
          const isMiddle = card.offset === 1 || (card.offset === 0 && isExiting);
          const isBack = card.offset === 2;

          let posClass = 'rejection-card--front';
          if (isMiddle && !isExiting) posClass = 'rejection-card--middle';
          if (isBack) posClass = 'rejection-card--back';
          if (isExiting) posClass = 'rejection-card--exiting';

          return (
            <div
              key={card.idx}
              className={`rejection-card ${posClass}`}
            >
              <div className="rejection-card-topbar">
                <span className="gs-rej-dot" /><span className="gs-rej-dot" /><span className="gs-rej-dot" />
              </div>
              <span className="rejection-card-company">{card.company}</span>
              {card.email && (
                <p className="rejection-card-email">{card.email}</p>
              )}
              <span
                className="rejection-card-pill"
                style={{ background: card.pillBg, color: card.pillColor }}
              >
                {card.pill}
              </span>
              {card.time && (
                <span className="rejection-card-time">{card.time}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClockBadge() {
  return (
    <div className="gs-clock-badge">
      <div className="gs-clock-circle">
        <ClockIcon size={20} />
      </div>
      <span className="gs-clock-label">15+ hrs/month</span>
    </div>
  );
}

function FunnelIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function MailIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 7L2 7" />
    </svg>
  );
}

function CalendarIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function SendIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function CleanCard() {
  const funnelStages = [
    { label: 'Applied', count: 23, color: '#2563EB', bg: '#DBEAFE' },
    { label: 'Screened', count: 14, color: '#2563EB', bg: '#DBEAFE' },
    { label: 'Interview', count: 6, color: '#059669', bg: '#ECFDF5' },
    { label: 'Offer', count: 2, color: '#059669', bg: '#ECFDF5' },
  ];

  const activityItems = [
    { icon: 'send', text: 'Applied to Stripe', detail: 'SWE Intern', time: '2m ago', color: '#2563EB' },
    { icon: 'mail', text: 'Cold email sent', detail: 'Google recruiter', time: '8m ago', color: '#2563EB' },
    { icon: 'calendar', text: 'Interview scheduled', detail: 'Amazon — SDE Intern', time: '1h ago', color: '#059669' },
    { icon: 'send', text: 'Applied to Meta', detail: 'ML Intern', time: '2h ago', color: '#2563EB' },
    { icon: 'mail', text: 'Follow-up sent', detail: 'Citadel recruiter', time: '3h ago', color: '#6B7280' },
  ];

  const iconMap = {
    send: <SendIcon size={12} />,
    mail: <MailIcon size={12} />,
    calendar: <CalendarIcon size={12} />,
  };

  return (
    <div className="ba-clean-card ba-clean-card--enriched">
      {/* Weekly progress header */}
      <div className="ba-card-header">
        <span className="ba-card-title">This Week</span>
        <span className="ba-count-badge">
          <FunnelIcon size={11} />
          {' '}23 sent
        </span>
      </div>

      {/* Funnel / pipeline visualization */}
      <div className="ba-funnel">
        {funnelStages.map((stage, i) => (
          <div key={stage.label} className="ba-funnel-stage">
            <div
              className="ba-funnel-bar"
              style={{
                width: `${Math.max(20, (stage.count / funnelStages[0].count) * 100)}%`,
                background: stage.bg,
                borderLeft: `3px solid ${stage.color}`,
              }}
            >
              <span className="ba-funnel-count" style={{ color: stage.color }}>{stage.count}</span>
              <span className="ba-funnel-label">{stage.label}</span>
            </div>
            {i < funnelStages.length - 1 && (
              <span className="ba-funnel-arrow" style={{ color: '#9CA3AF' }}>&#8595;</span>
            )}
          </div>
        ))}
      </div>

      {/* Activity feed */}
      <div className="ba-activity-feed">
        <span className="ba-activity-title">Live Activity</span>
        {activityItems.map((item, i) => (
          <div key={i} className="ba-activity-item">
            <span className="ba-activity-icon" style={{ color: item.color }}>
              {iconMap[item.icon]}
            </span>
            <div className="ba-activity-text">
              <span className="ba-activity-main">{item.text}</span>
              <span className="ba-activity-detail">{item.detail}</span>
            </div>
            <span className="ba-activity-time">{item.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main component ── */

function BeforeAfter() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef, 0.2);

  const [phase, setPhase] = useState(0);

  /* Rejection interaction state */
  const [currentCard, setCurrentCard] = useState(0);
  const [deadRows, setDeadRows] = useState(new Set());
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [rejPhase, setRejPhase] = useState('clicking'); // clicking | pause | rapid | aftermath
  const [promptVisible, setPromptVisible] = useState(true);
  const [showAftermathToast, setShowAftermathToast] = useState(false);
  const [rightGlow, setRightGlow] = useState(false);
  const [, setShowReplay] = useState(false);
  const [showPauseBtn, setShowPauseBtn] = useState(false);
  const animatingRef = useRef(false);

  useEffect(() => {
    if (!visible) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReduced) {
      setPhase(99);
      return;
    }
    const timers = [
      setTimeout(() => setPhase(1), 0),
      setTimeout(() => setPhase(2), 200),
      setTimeout(() => setPhase(3), 400),
      setTimeout(() => setPhase(4), 600),
      setTimeout(() => setPhase(5), 800),
      setTimeout(() => setPhase(6), 1000),
      setTimeout(() => setPhase(7), 1200),
      setTimeout(() => setPhase(8), 1400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [visible]);

  const atLeast = (n) => phase >= n || phase === 99;

  /* Kill a spreadsheet row with highlight flash then death */
  const killRow = (rowNum) => {
    setHighlightedRow(rowNum);
    setTimeout(() => {
      setHighlightedRow(null);
      setDeadRows((prev) => new Set(prev).add(rowNum));
    }, 200);
  };

  /* Auto-dismiss: runs automatically once section is visible */
  useEffect(() => {
    if (!visible || rejPhase !== 'clicking') return;

    const startDelay = setTimeout(() => {
      setPromptVisible(false);
      let idx = 0;

      const dismissNext = () => {
        if (idx >= 12) {
          setRejPhase('aftermath');
          return;
        }
        const rowToKill = CARD_TO_ROW[idx];
        killRow(rowToKill);
        setTimeout(() => {
          idx++;
          setCurrentCard(idx);
          if (idx < 12) {
            setTimeout(dismissNext, 1200);
          } else {
            setRejPhase('aftermath');
          }
        }, 300);
      };

      dismissNext();
    }, 2000);

    return () => clearTimeout(startDelay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, rejPhase]);

  /* No-ops to prevent unused warnings */
  const handleDismiss = () => {};
  void showPauseBtn;

  /* Aftermath effects */
  useEffect(() => {
    if (rejPhase !== 'aftermath') return;
    const glowTimer = setTimeout(() => setRightGlow(true), 1000);
    const toastTimer = setTimeout(() => setShowAftermathToast(true), 1000);
    const replayTimer = setTimeout(() => setShowReplay(true), 3000);
    return () => {
      clearTimeout(glowTimer);
      clearTimeout(toastTimer);
      clearTimeout(replayTimer);
    };
  }, [rejPhase]);

  /* Reset everything */
  const handleReplay = () => {
    setCurrentCard(0);
    setDeadRows(new Set());
    setHighlightedRow(null);
    setRejPhase('clicking');
    setPromptVisible(true);
    setShowAftermathToast(false);
    setRightGlow(false);
    setShowReplay(false);
    setShowPauseBtn(false);
    animatingRef.current = false;
  };

  return (
    <section className="ba-section" ref={sectionRef}>
      <div className="ba-inner">
        {/* Header */}
        <div className={`ba-header-group ${atLeast(1) ? 'ba-fade-in' : 'ba-hidden'}`}>
          <p className="ba-kicker">WHY APPLYAI</p>
          <h2 className="ba-heading">
            Same goal. Two very different paths.
          </h2>
          <p className="ba-subtitle">
            One side is manual. The other runs while you sleep.
          </p>
        </div>

        {/* Comparison */}
        <div className={`ba-comparison ${atLeast(2) ? 'ba-fade-in' : 'ba-hidden'}`}>
          {/* Left — Without */}
          <div className={`ba-left ${atLeast(3) ? 'ba-scattered' : ''}`}>
            <p className="ba-label ba-label--left">
              <XSmall />
              <span>Without ApplyAI</span>
            </p>
            <div className="ba-left-content">
              <GoogleSheetsWindow deadRows={deadRows} highlightedRow={highlightedRow} />
              <RejectionStack
                currentCard={currentCard}
                rejPhase={rejPhase}
                promptVisible={promptVisible}
                onDismiss={handleDismiss}
                onShowAll={() => {}}
                onReplay={handleReplay}
              />
            </div>
            <ClockBadge />
          </div>

          {/* Divider */}
          <div className="ba-divider">
            <div className="ba-divider-bar" />
            <div className="ba-divider-handle">
              <span className="ba-divider-arrows">&lsaquo;&rsaquo;</span>
            </div>
            <div className="ba-divider-bar" />
          </div>

          {/* Right — With */}
          <div className={`ba-right ${rightGlow ? 'ba-right--glow' : ''}`}>
            <p className="ba-label ba-label--right">
              <CheckSmall />
              <span>With ApplyAI</span>
            </p>
            <div className="ba-right-content">
              <div className={`ba-clean-card-wrapper ba-clean-card-wrapper--wide ${atLeast(4) ? 'ba-scale-in' : 'ba-hidden'}`}>
                <CleanCard />
              </div>
              {showAftermathToast && (
                <div className="ba-toasts">
                  <div className="ba-toast ba-toast--green ba-toast--visible ba-toast--aftermath">
                    &#10003; 3 new interviews scheduled while you were away
                  </div>
                </div>
              )}
            </div>
            <div className="ba-clock-badge ba-clock-badge--green">
              <div className="ba-clock-ring ba-clock-ring--green">
                <CheckSmall />
              </div>
              <span className="ba-clock-text ba-clock-text--green">5 min setup</span>
            </div>
          </div>
        </div>

        {/* Stat bar */}
        <div className={`ba-stats ${atLeast(8) ? 'ba-fade-in' : 'ba-hidden'}`}>
          <div className="ba-stat">
            <span className="ba-stat-icon"><ClockIcon size={24} /></span>
            <CountUp target={15} suffix="+" delay={0} />
            <span className="ba-stat-desc">hours saved per month</span>
          </div>
          <div className="ba-stat-divider" />
          <div className="ba-stat">
            <span className="ba-stat-icon"><DocIcon size={24} /></span>
            <CountUp target={10} suffix="x" delay={200} />
            <span className="ba-stat-desc">more applications</span>
          </div>
          <div className="ba-stat-divider" />
          <div className="ba-stat">
            <span className="ba-stat-icon"><PhoneIcon size={24} /></span>
            <CountUp target={3} suffix="x" delay={400} />
            <span className="ba-stat-desc">more callbacks</span>
          </div>
          <div className="ba-stat-divider" />
          <div className="ba-stat">
            <span className="ba-stat-icon"><TrophyIcon size={24} /></span>
            <CountUp target={500} suffix="+" delay={600} />
            <span className="ba-stat-desc">offers landed</span>
            <span className="ba-stat-context">across 5,000+ users</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BeforeAfter;
